import { getContrastingTextColor } from '@chatwoot/utils';
import { getDarkMode } from '../../sdk/settingsHelper';

const LEGACY_FONT_SIZES = {
  small: '14px',
  medium: '16px',
  large: '18px',
};

export const MIN_FONT_SIZE = 10;
export const MAX_FONT_SIZE = 32;
export const DEFAULT_FONT_SIZE_PX = 16;
export const DEFAULT_WIDGET_COLOR = '#20BDB0';
const DEFAULT_FONT_SIZE = `${DEFAULT_FONT_SIZE_PX}px`;

const HEX_COLOR_PATTERN = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

export const getChannelConfig = () => window.chatwootWebChannel || {};

export const getChannelAppearance = () => {
  const channel = getChannelConfig();

  return {
    widgetColor: channel.widgetColor,
    darkMode: channel.darkMode,
    fontSize: channel.widgetFontSize,
    fontColor: channel.widgetFontColor,
    agentBubbleColor: channel.widgetAgentBubbleColor,
  };
};

export const isWidgetDark = () =>
  document.documentElement.classList.contains('dark');

export const getDefaultAgentTextColor = (isDark = isWidgetDark()) =>
  isDark ? '#ececec' : '#0d0d0d';

export const getDefaultAgentBubbleColor = (isDark = isWidgetDark()) =>
  isDark ? '#2a2a2a' : '#ffffff';

export const resolveWidgetFontSize = fontSize => {
  if (fontSize === undefined || fontSize === null || fontSize === '') {
    return DEFAULT_FONT_SIZE;
  }

  const normalized = String(fontSize).trim();

  if (LEGACY_FONT_SIZES[normalized]) {
    return LEGACY_FONT_SIZES[normalized];
  }

  const pxValue = parseInt(normalized, 10);
  if (!Number.isNaN(pxValue)) {
    const clamped = Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, pxValue));
    return `${clamped}px`;
  }

  return DEFAULT_FONT_SIZE;
};

export const resolveCustomColor = color =>
  color && HEX_COLOR_PATTERN.test(color) ? color : null;

export const resolveFontColor = () => {
  const { fontColor } = getChannelAppearance();
  return resolveCustomColor(fontColor);
};

export const applyWidgetColor = widgetColor => {
  const color = widgetColor || getChannelConfig().widgetColor;
  if (!color) return;

  document.documentElement.style.setProperty('--widget-color', color);
};

export const applyWidgetTypography = ({ fontSize, fontColor } = {}) => {
  document.documentElement.style.setProperty(
    '--widget-font-size',
    resolveWidgetFontSize(fontSize)
  );

  const resolvedFontColor = resolveCustomColor(fontColor);
  if (resolvedFontColor) {
    document.documentElement.style.setProperty(
      '--widget-font-color',
      resolvedFontColor
    );
  } else {
    document.documentElement.style.removeProperty('--widget-font-color');
  }
};

export const applyChannelDarkMode = (darkMode, { setColorScheme } = {}) => {
  const resolvedMode = getDarkMode(darkMode || 'light');
  if (setColorScheme) setColorScheme(resolvedMode);

  const prefersDark =
    resolvedMode === 'dark' ||
    (resolvedMode === 'auto' &&
      (window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false));

  document.documentElement.classList.toggle('dark', prefersDark);
  document.documentElement.style.colorScheme = prefersDark ? 'dark' : 'light';

  return resolvedMode;
};

export const applyChannelAppearance = ({
  setColorScheme,
  sdkDarkMode,
} = {}) => {
  const { widgetColor, darkMode, fontSize, fontColor } = getChannelAppearance();
  const darkModeToApply =
    sdkDarkMode !== undefined && sdkDarkMode !== null ? sdkDarkMode : darkMode;

  applyWidgetColor(widgetColor);
  applyChannelDarkMode(darkModeToApply, { setColorScheme });
  applyWidgetTypography({ fontSize, fontColor });
};

export const getUserBubbleStyle = widgetColor => {
  const { fontSize, widgetColor: channelColor } = getChannelAppearance();
  const backgroundColor = widgetColor || channelColor || DEFAULT_WIDGET_COLOR;
  const customTextColor = resolveFontColor();

  return {
    background: backgroundColor,
    color: customTextColor || getContrastingTextColor(backgroundColor),
    fontSize: resolveWidgetFontSize(fontSize),
  };
};

export const getAgentBubbleStyle = () => {
  const { agentBubbleColor, fontSize } = getChannelAppearance();
  const isDark = isWidgetDark();
  const customTextColor = resolveFontColor();
  const style = {
    fontSize: resolveWidgetFontSize(fontSize),
    color: customTextColor || getDefaultAgentTextColor(isDark),
  };

  const bubbleColor = resolveCustomColor(agentBubbleColor);
  if (bubbleColor) {
    style.backgroundColor = bubbleColor;
  }

  return style;
};

export const getAgentMessageContentStyle = () => {
  const customColor = resolveFontColor();

  if (!customColor) return {};

  return { color: customColor };
};

export const getWidgetInputStyle = () => {
  const { fontSize } = getChannelAppearance();

  return {
    fontSize: resolveWidgetFontSize(fontSize),
    color: resolveFontColor() || getDefaultAgentTextColor(),
  };
};
